<template>
  <div class="menu-search-container position-relative">
    <!-- Caja de texto con borde rojo -->
    <input
      type="text"
      v-model="searchQuery"
      @input="filterMenuItems"
      @keydown.enter="handleEnterKey"
      @keydown.up.prevent="handleArrowUp"
      @keydown.down.prevent="handleArrowDown"
      @focus="showDropdown = true"
      @blur="handleBlur"
      class="form-control menu-search-input"
      placeholder="Buscar en el menú..."
      autocomplete="off"
    />
    
    <!-- Dropdown con coincidencias -->
    <div 
      v-if="showDropdown && filteredItems.length > 0" 
      class="dropdown-menu show menu-search-dropdown"
    >
      <div 
        v-for="(item, index) in filteredItems" 
        :key="item.Menu_id"
        @mousedown="selectMenuItem(item)"
        class="dropdown-item menu-search-item"
        :class="{ 'active': index === selectedIndex, 'selected': selectedIndex === index }"
      >
        <i :class="getIconClass(item.Icono)" class="me-2"></i>
        <span class="menu-title">{{ item.Titulo }}</span>
        <small class="text-muted ms-2">{{ item.Descripcion || '' }}</small>
      </div>
    </div>
    
    <!-- Mensaje cuando no hay coincidencias -->
    <div 
      v-if="showDropdown && searchQuery && filteredItems.length === 0" 
      class="dropdown-menu show menu-search-dropdown"
    >
      <div class="dropdown-item-text text-muted">
        <i class="bi bi-search me-2"></i>
        No se encontraron coincidencias
      </div>
    </div>
  </div>
</template>

<script>
import authService from '@/shared/services/auth';
import { formatRoute } from '@/shared/utils/route-helper';

export default {
  name: 'MenuSearchBox',
  data() {
    return {
      searchQuery: '',
      filteredItems: [],
      allMenuItems: [],
      showDropdown: false,
      selectedIndex: -1
    };
  },
  mounted() {
    this.loadMenuItems();
  },
  methods: {
    /**
     * Cargar todos los elementos del menú del usuario
     */
    loadMenuItems() {
      const userMenu = authService.getUserMenu();
      if (userMenu && Array.isArray(userMenu)) {
        // Filtrar solo elementos que tengan enlace válido
        this.allMenuItems = userMenu.filter(item => 
          item.Enlace && 
          item.Enlace.trim() !== '' && 
          item.Titulo && 
          item.Titulo.trim() !== ''
        );
      }
    },
    
    /**
     * Filtrar elementos del menú basado en la consulta de búsqueda
     */
    filterMenuItems() {
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        this.filteredItems = [];
        this.selectedIndex = -1;
        return;
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      
      this.filteredItems = this.allMenuItems.filter(item => {
        const title = (item.Titulo || '').toLowerCase();
        const description = (item.Descripcion || '').toLowerCase();
        
        return title.includes(query) || description.includes(query);
      }).slice(0, 10); // Limitar a 10 resultados
      
      this.selectedIndex = -1;
    },
    
    /**
     * Manejar la tecla Enter
     */
    handleEnterKey() {
      if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredItems.length) {
        // Si hay un elemento seleccionado con las teclas de flecha
        this.selectMenuItem(this.filteredItems[this.selectedIndex]);
      } else if (this.filteredItems.length === 1) {
        // Si hay exactamente una coincidencia, navegar automáticamente
        this.selectMenuItem(this.filteredItems[0]);
      }
    },
    
    /**
     * Seleccionar un elemento del menú y navegar
     */
    selectMenuItem(item) {
      if (!item || !item.Enlace) return;
      
      const route = formatRoute(item.Enlace);
      
      // Limpiar la búsqueda
      this.searchQuery = '';
      this.filteredItems = [];
      this.showDropdown = false;
      this.selectedIndex = -1;
      
      // Navegar a la ruta
      this.$router.push(route).catch(err => {
        console.warn('Error al navegar:', err);
      });
    },
    
    /**
     * Manejar el evento blur del input
     */
    handleBlur() {
      // Usar setTimeout para permitir que el click en el dropdown funcione
      setTimeout(() => {
        this.showDropdown = false;
        this.selectedIndex = -1;
      }, 200);
    },

    handleArrowUp() {
      if (this.filteredItems.length === 0) return;
      
      this.selectedIndex = this.selectedIndex <= 0 
        ? this.filteredItems.length - 1 
        : this.selectedIndex - 1;
      
      this.scrollToSelected();
    },

    handleArrowDown() {
      if (this.filteredItems.length === 0) return;
      
      this.selectedIndex = this.selectedIndex >= this.filteredItems.length - 1 
        ? 0 
        : this.selectedIndex + 1;
      
      this.scrollToSelected();
    },

    scrollToSelected() {
      this.$nextTick(() => {
        const dropdown = this.$el.querySelector('.menu-search-dropdown');
        const selectedItem = this.$el.querySelector('.menu-search-item.selected');
        
        if (dropdown && selectedItem) {
          const dropdownRect = dropdown.getBoundingClientRect();
          const itemRect = selectedItem.getBoundingClientRect();
          
          if (itemRect.bottom > dropdownRect.bottom) {
            selectedItem.scrollIntoView({ block: 'end', behavior: 'smooth' });
          } else if (itemRect.top < dropdownRect.top) {
            selectedItem.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        }
      });
    },
    
    /**
     * Obtener la clase CSS para el ícono
     */
    getIconClass(iconName) {
      if (!iconName) return 'bi bi-circle';
      
      // Si ya tiene el prefijo bi-, devolverlo tal como está
      if (iconName.startsWith('bi-')) {
        return `bi ${iconName}`;
      }
      
      // Si no tiene prefijo, agregarlo
      return `bi bi-${iconName}`;
    }
  },
  
  watch: {
    // Recargar elementos del menú si cambia la autenticación
    '$route'() {
      this.loadMenuItems();
    }
  }
};
</script>